import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, ExternalLink } from 'lucide-react';
import { useMotionMode } from '../../contexts/MotionContext';
import { RevealText, Reveal } from '../motion/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleG = ({ className = 'h-5 w-5' }) => (
  <svg viewBox="0 0 48 48" className={className} aria-label="Google">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const Stars = ({ n, className = 'h-4 w-4' }) => (
  <div className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={`star-${i}`} className={`${className} ${i <= n ? 'fill-white text-white' : 'text-white/30'}`} />)}</div>
);

const ReviewCard = ({ r, mapsUri }) => (
  <figure className="shrink-0 w-[340px] md:w-[420px] mx-3 p-8 bg-black border border-white/15 hover:border-white/40 hover:-rotate-1 hover:scale-[1.03] transition-[transform,border-color] duration-500 flex flex-col">
    <div className="flex items-center justify-between mb-5">
      <Stars n={r.rating} />
      <GoogleG className="h-4 w-4" />
    </div>
    <blockquote className="text-white/80 leading-relaxed line-clamp-6 flex-1">“{r.text}”</blockquote>
    <figcaption className="mt-6 flex items-center gap-3">
      {r.photo && <img src={r.photo} alt="" referrerPolicy="no-referrer" className="h-10 w-10 rounded-full object-cover" />}
      <div className="min-w-0">
        <a href={r.authorUri || mapsUri} target="_blank" rel="noreferrer" className="font-semibold hover:underline">{r.author}</a>
        <div className="text-xs text-white/50">{r.relativeTime} · on Google</div>
      </div>
    </figcaption>
  </figure>
);

const Track = ({ reviews, mapsUri, reverse, duration }) => (
  <div className="flex w-max oc-marquee" style={{ animationDuration: `${duration}s`, animationDirection: reverse ? 'reverse' : 'normal' }}>
    {[0, 1, 2, 3].flatMap((copy) => reviews.map((r) => <ReviewCard key={`${copy}-${r.publishTime || r.author}`} r={r} mapsUri={mapsUri} />))}
  </div>
);

export const GoogleReviews = () => {
  const { reduced } = useMotionMode();
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/google-reviews`).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  const reviews = (data?.reviews || []).filter((r) => r.text);
  if (!reviews.length) return null;

  return (
    <section className="py-24 lg:py-32 overflow-hidden bg-neutral-950 text-white" data-testid="home-testimonials">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] mb-4 text-white/50">Google Reviews</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight"><RevealText text="What Our Clients Say" /></h2>
        </div>
        <Reveal>
          <a href={data.mapsUri} target="_blank" rel="noreferrer" className="group flex items-center gap-4 border border-white/15 px-5 py-4 hover:border-white/40 transition-colors" data-testid="google-reviews-link">
            <GoogleG className="h-8 w-8" />
            <div>
              <div className="flex items-center gap-2"><span className="text-2xl font-bold" data-testid="google-rating">{Number(data.rating).toFixed(1)}</span><Stars n={Math.round(data.rating)} /></div>
              <div className="text-xs text-white/60 flex items-center gap-1" data-testid="google-review-count">Based on {data.total} Google reviews <ExternalLink className="h-3 w-3 transition-transform group-hover:translate-x-0.5" /></div>
            </div>
          </a>
        </Reveal>
      </div>
      {reduced ? (
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-center gap-6">{reviews.map((r) => <ReviewCard key={r.publishTime || r.author} r={r} mapsUri={data.mapsUri} />)}</div>
      ) : (
        <div className="space-y-6 [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]" data-testid="testimonial-marquee">
          <Track reviews={reviews} mapsUri={data.mapsUri} duration={70} />
          <Track reviews={[...reviews].reverse()} mapsUri={data.mapsUri} reverse duration={80} />
        </div>
      )}
    </section>
  );
};
